import { FC, useState, useMemo, useCallback } from "react"
import { DailyPoint } from "../../../app/reducers/analyticsSlice"

export const COLORS = {
  revenue: "#2b8a3e",
  payout: "#1971c2",
  returnPayout: "#c92a2a",
  compensationPayout: "#7048e8",
  profit: "#f59f00",
  netProfit: "#e64980",
  logistics: "#e8590c",
  commission: "#ae3ec9",
  storage: "#868e96",
  penalty: "#c92a2a",
  deduction: "#495057",
  acquiring: "#0c8599",
}

const LEGEND_LABELS: Record<keyof typeof COLORS, string> = {
  revenue: "Выручка",
  payout: "К выплате (продажи)",
  returnPayout: "Удержано за возвраты",
  compensationPayout: "Компенсации/корректировки",
  profit: "Валовая прибыль",
  netProfit: "Чистая прибыль",
  logistics: "Логистика",
  commission: "Комиссия",
  storage: "Хранение",
  penalty: "Штрафы",
  deduction: "Удержания",
  acquiring: "Эквайринг",
}

type LineChartProps = { data: DailyPoint[] }

export const LineChart: FC<LineChartProps> = ({ data }) => {
  const [hidden, setHidden] = useState<Set<string>>(
    new Set([
      "deduction",
      "acquiring",
      "penalty",
      "returnPayout",
      "compensationPayout",
    ]),
  )

  const W = 900
  const H = 280
  const PL = 72
  const PR = 20
  const PT = 10
  const PB = 38
  const cw = W - PL - PR
  const ch = H - PT - PB

  const toggle = useCallback((key: string) => {
    setHidden((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }, [])

  const allLines: { key: keyof typeof COLORS; values: number[] }[] = useMemo(
    () => [
      { key: "revenue", values: data.map((d) => d.revenue) },
      { key: "payout", values: data.map((d) => d.payoutSales) },
      { key: "returnPayout", values: data.map((d) => -d.returnPayout) },
      {
        key: "compensationPayout",
        values: data.map((d) => d.compensationPayout),
      },
      { key: "profit", values: data.map((d) => d.profit) },
      { key: "netProfit", values: data.map((d) => d.netProfit) },
      { key: "logistics", values: data.map((d) => d.logistics) },
      { key: "commission", values: data.map((d) => d.commission) },
      { key: "storage", values: data.map((d) => d.storage) },
      { key: "penalty", values: data.map((d) => d.penalty) },
      { key: "deduction", values: data.map((d) => d.deduction) },
      { key: "acquiring", values: data.map((d) => d.acquiring) },
    ],
    [data],
  )

  const visibleVals = useMemo(
    () => allLines.filter((l) => !hidden.has(l.key)).flatMap((l) => l.values),
    [allLines, hidden],
  )

  const rawMax = Math.max(...visibleVals, 1)
  const rawMin = Math.min(...visibleVals, 0)
  const pad = (rawMax - rawMin || 1) * 0.05
  const maxVal = rawMax + pad
  const minVal = rawMin - pad
  const totalRange = maxVal - minVal

  const stepX = data.length > 1 ? cw / (data.length - 1) : cw

  const toY = useCallback(
    (v: number) => PT + ch - ((v - minVal) / totalRange) * ch,
    [minVal, totalRange, ch],
  )

  const toPath = useCallback(
    (values: number[]) =>
      values
        .map(
          (v, i) =>
            `${i === 0 ? "M" : "L"}${(PL + i * stepX).toFixed(1)},${toY(
              v,
            ).toFixed(1)}`,
        )
        .join(" "),
    [stepX, toY],
  )

  const yTicks = useMemo(() => {
    const ticks: number[] = []
    for (let i = 0; i <= 4; i++) ticks.push(minVal + (totalRange / 4) * i)
    return ticks
  }, [minVal, totalRange])

  const zeroY = toY(0)

  return (
    <div>
      <div className="chart-legend">
        {allLines.map(({ key }) => (
          <span
            key={key}
            className="legend-item"
            style={{ cursor: "pointer", opacity: hidden.has(key) ? 0.3 : 1 }}
            onClick={() => toggle(key)}
          >
            <span className="legend-dot" style={{ background: COLORS[key] }} />
            {LEGEND_LABELS[key]}
          </span>
        ))}
        <span style={{ fontSize: 11, color: "var(--gray-7)", marginLeft: 4 }}>
          — клик чтобы скрыть
        </span>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="svg-chart"
        preserveAspectRatio="xMidYMid meet"
      >
        {zeroY >= PT && zeroY <= PT + ch && (
          <line
            x1={PL}
            x2={W - PR}
            y1={zeroY}
            y2={zeroY}
            stroke="#ced4da"
            strokeWidth={1.5}
            strokeDasharray="5,3"
          />
        )}
        {yTicks.map((t, i) => {
          const y = toY(t)
          return (
            <g key={i}>
              <line
                x1={PL}
                x2={W - PR}
                y1={y}
                y2={y}
                stroke="#f1f3f5"
                strokeWidth={1}
              />
              <text
                x={PL - 6}
                y={y + 4}
                textAnchor="end"
                fontSize={10}
                fill="#868e96"
              >
                {Math.abs(t) >= 1000
                  ? (t / 1000).toFixed(0) + "k"
                  : Math.round(t)}
              </text>
            </g>
          )
        })}
        {data.map((d, i) => {
          const x = PL + i * stepX
          const show =
            data.length <= 31 || i % Math.ceil(data.length / 14) === 0
          return show ? (
            <text
              key={i}
              x={x}
              y={H - 6}
              textAnchor="middle"
              fontSize={9}
              fill="#868e96"
            >
              {d.date.slice(5)}
            </text>
          ) : null
        })}
        {allLines.map((l) =>
          hidden.has(l.key) ? null : (
            <path
              key={l.key}
              d={toPath(l.values)}
              fill="none"
              stroke={COLORS[l.key]}
              strokeWidth={
                l.key === "netProfit" ? 2.5 : l.key === "profit" ? 2 : 1.8
              }
              strokeLinejoin="round"
              strokeDasharray={l.key === "netProfit" ? "6,3" : undefined}
            />
          ),
        )}
      </svg>
    </div>
  )
}
